import CustomTooltip from '../../../../shared/CustomTooltip';
import { Box, Typography, lighten, Button } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import React, { useEffect, useMemo, useState } from 'react';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { theme } from '../../../../../shared/theme';
import ShareReservation from '../ShareReservation';
import { CustomChip } from '../../../../../layout/styledcomponents/CustomChip';
import {
    useGetUserReviewForBookingQuery,
    useGetReviewsQuery,
    useGetGuestReviewsQuery,
} from '../../../../../app/Slices/SevenDayApiSlice';
import ReviewModal from '../../../ExperienceReviews/ReviewModal';
import ReviewsModalForReservation from '../ReviewsModalForReservation';
import GuestReviewModal from '../GuestReviewModal';

type ReservationSummarySectionProps = {
    getReservationDetails: any;
    getIndividualAsset: any;
    loggedInUser: any;
    startDate: string;
    endDate: string;
    calendarStart: string;
    calendarEnd: string;
    calDescriptionActivity: string;
    calDescriptionVehicle: string;
    calDescriptionCommercial: string;
    calDescriptionNonVehicle: string;
    fullAddress: string;
};

const formatTimeWithAmPm = (t: any) => {
    const raw = String(t || '').trim();
    if (!raw || raw === '-') return '-';
    const m = moment(
        raw,
        ['H:mm', 'HH:mm', 'h:mm A', 'h:mma', 'hh:mm A'],
        true,
    );
    if (m.isValid()) return m.format('h:mm A');
    return raw;
};

export default function ReservationSummarySection({
    getReservationDetails,
    getIndividualAsset,
    loggedInUser,
    calendarStart,
    calendarEnd,
    calDescriptionActivity,
    calDescriptionVehicle,
    calDescriptionCommercial,
    calDescriptionNonVehicle,
    fullAddress,
}: ReservationSummarySectionProps) {
    const status = getReservationDetails?.status ?? '';
    const [openReviewModal, setOpenReviewModal] = useState(false);
    const [openViewReviewsModal, setOpenViewReviewsModal] = useState(false);
    const [openGuestReviewModal, setOpenGuestReviewModal] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const isOwner = loggedInUser?.role?.includes('owner');
    const isGuest = loggedInUser?.role?.includes('guest');

    const { data: allReviews } = useGetReviewsQuery(
        { bookingId: getReservationDetails?.id },
        {
            skip:
                !getReservationDetails?.id ||
                getReservationDetails?.status !== 'completed' ||
                !isOwner,
        },
    );

    const {
        data: existingReview,
        isLoading: isExistingReviewLoading,
        isFetching: isExistingReviewFetching,
    } = useGetUserReviewForBookingQuery(getReservationDetails?.id, {
        skip:
            !getReservationDetails?.id ||
            getReservationDetails?.status !== 'completed' ||
            isOwner,
    });

    const { data: guestReviews } = useGetGuestReviewsQuery(
        { bookingId: getReservationDetails?.id },
        {
            skip:
                !getReservationDetails?.id ||
                getReservationDetails?.status !== 'completed' ||
                (!isOwner && !isGuest),
        },
    );

    const existingGuestReview = guestReviews?.[0];
    const isGuestReviewPublished = Boolean(existingReview?.published);
    const isGuestReviewPending = !isOwner && isExistingReviewLoading;

    useEffect(() => {
        const reviewParam = searchParams.get('review');
        const showReviewsParam = searchParams.get('showReviews');
        const showGuestReviewParam = searchParams.get('showGuestReview');

        if (reviewParam === 'true') {
            if (isGuestReviewPending) {
                setOpenViewReviewsModal(true);
                setOpenReviewModal(false);
                return;
            }
            if (isGuestReviewPublished) {
                setOpenViewReviewsModal(true);
                setOpenReviewModal(false);
            } else {
                setOpenViewReviewsModal(false);
                setOpenReviewModal(true);
            }
        }
        if (showReviewsParam === 'true') setOpenViewReviewsModal(true);
        if (showGuestReviewParam === 'true') setOpenGuestReviewModal(true);
    }, [searchParams, isGuestReviewPending, isGuestReviewPublished]);

    const handleViewReviews = () => {
        setOpenViewReviewsModal(true);
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set('showReviews', 'true');
            newParams.delete('review');
            return newParams;
        });
    };

    const handleReview = () => {
        if (isGuestReviewPending) {
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set('review', 'true');
                return newParams;
            });
            return;
        }
        if (isGuestReviewPublished) {
            handleViewReviews();
            return;
        }
        setOpenReviewModal(true);
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set('review', 'true');
            return newParams;
        });
    };

    const handleCloseReviewModal = () => {
        setOpenReviewModal(false);
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('review');
            newParams.delete('rating');
            return newParams;
        });
    };

    const handleCloseViewReviewsModal = () => {
        setOpenViewReviewsModal(false);
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('showReviews');
            newParams.delete('review');
            return newParams;
        });
    };

    const handleOpenGuestReview = () => {
        setOpenGuestReviewModal(true);
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set('showGuestReview', 'true');
            return newParams;
        });
    };

    const handleCloseGuestReviewModal = () => {
        setOpenGuestReviewModal(false);
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('showGuestReview');
            return newParams;
        });
    };

    const reviewCutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const canReview =
        new Date(getReservationDetails?.endDate) > reviewCutoff &&
        getReservationDetails?.status === 'completed';
    const canReviewGuest =
        isOwner && canReview && Boolean(getReservationDetails?.guest?.id);

    const formattedStatus =
        getReservationDetails?.status?.charAt(0).toUpperCase() +
        getReservationDetails?.status?.slice(1);

    const showCheckTimes = !(
        getReservationDetails?.bookerType === 'owner' &&
        getReservationDetails?.onBehalf === false
    );

    const checkinTime = showCheckTimes
        ? getReservationDetails?.bookingInfo?.checkinTime
        : '-';

    const checkoutTime = showCheckTimes
        ? getReservationDetails?.bookingInfo?.checkoutTime
        : '-';

    const isCommercial = getIndividualAsset?.type === 'commercial';
    const startDateRaw = getReservationDetails?.startDate;
    const endDateRaw = getReservationDetails?.endDate;

    const dateRange = useMemo(() => {
        if (!startDateRaw || !endDateRaw) return '-';
        const s = moment(startDateRaw);
        const e = moment(endDateRaw);
        if (!s.isValid() || !e.isValid()) return '-';
        if (s.isSame(e, 'day')) return s.format('ddd D MMM YYYY');
        if (s.isSame(e, 'month'))
            return `${s.format('ddd D')} – ${e.format('ddd D MMM YYYY')}`;
        return `${s.format('ddd D MMM')} – ${e.format('ddd D MMM YYYY')}`;
    }, [startDateRaw, endDateRaw]);

    const timeRange = useMemo(() => {
        const s = formatTimeWithAmPm(checkinTime);
        const e = formatTimeWithAmPm(checkoutTime);
        if (!showCheckTimes) return '-';
        if (s === '-' && e === '-') return '-';
        if (s !== '-' && e !== '-') return `${s} → ${e}`;
        return s !== '-' ? s : e;
    }, [checkinTime, checkoutTime, showCheckTimes]);

    const getStatusSx = (s: string) => {
        switch (s) {
            case 'approved':
                return {
                    bgcolor: lighten(theme.palette.yellow.main, 0.78),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.yellow.main, 0.35),
                };
            case 'pending':
                return {
                    bgcolor: lighten(theme.palette.primary.main, 0.76),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.primary.main, 0.32),
                };
            case 'cancelled':
            case 'declined':
                return {
                    bgcolor: lighten(theme.palette.error.main, 0.82),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.error.main, 0.3),
                };
            case 'completed':
                return {
                    bgcolor: lighten(theme.palette.info.main, 0.82),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.info.main, 0.3),
                };
            default:
                return {
                    bgcolor: theme.palette.custom.cardActionBackground,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.custom.lightBorderColor,
                };
        }
    };

    const calendarIconStyle = `
  --btn-background: transparent;
  --btn-border: none;
  --btn-shadow: none;
  --btn-hover-background: transparent;
  --btn-hover-border: none;
  --btn-hover-shadow: none;
  --btn-text: transparent;
  --btn-hover-text: transparent;
  --btn-padding: 0;
  --btn-border-radius: 0;
`;

    const statusLower = String(status || '').toLowerCase();

    const canShowCalendarTool =
        !loggedInUser?.role?.includes('owner') &&
        statusLower !== 'cancelled' &&
        statusLower !== 'declined' &&
        statusLower !== 'completed';
    const canShowDownloadTool = ['pending', 'approved', 'completed'].includes(
        statusLower,
    );

    const calDescription =
        getReservationDetails?.module?.type === 'activity'
            ? calDescriptionActivity
            : getReservationDetails?.module?.type === 'vehicle'
              ? calDescriptionVehicle
              : getReservationDetails?.module?.type === 'commercial'
                ? calDescriptionCommercial
                : calDescriptionNonVehicle;

    const titleName = `${
        loggedInUser?.role.includes('owner')
            ? `${
                  getReservationDetails?.guest?.name
                      ? getReservationDetails?.guest?.name
                      : getReservationDetails?.owner?.name
              } - ${getReservationDetails?.module?.name} REF: #${getReservationDetails?.id}`
            : `${getReservationDetails?.module?.name} REF: #${getReservationDetails?.id}`
    }`;

    const icalName = `${
        loggedInUser?.role.includes('owner')
            ? `${
                  getReservationDetails?.guest?.name
                      ? getReservationDetails?.guest?.name
                      : getReservationDetails?.owner?.name
              } - ${getReservationDetails?.module?.name}-${getReservationDetails?.status} reservation REF: #${getReservationDetails?.id}`
            : `${getReservationDetails?.module?.name}-${getReservationDetails?.status} reservation REF: #${getReservationDetails?.id}`
    }`;

    const canBookAgain =
        !loggedInUser?.role?.includes('owner') &&
        getReservationDetails?.module?.isAvailable !== false;

    const moduleType = getReservationDetails?.module?.type;
    const moduleId = getReservationDetails?.module?.id;

    const handleBookAgain = () => {
        if (!moduleType || !moduleId) return;
        navigate(`/viewExperiences/${moduleType}/${moduleId}`);
    };

    const hasExistingReview = Boolean(existingReview) || allReviews?.length > 0;

    const iconButtonSx = {
        width: 30,
        height: 30,
        borderRadius: 2,
        display: 'grid',
        placeItems: 'center',
        opacity: 0.98,
        position: 'relative' as const,
        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
        backgroundColor: theme.palette.custom.cardActionBackground,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
        },
    };

    const infoCardSx = {
        width: '100%',
        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
        backgroundColor: theme.palette.neutral.main,
        borderRadius: 2,
        p: 2,
    };

    const pillButtonSx = {
        borderRadius: 2,
        height: 36,
        fontWeight: 900,
        textTransform: 'none' as const,
    };

    const openRecurringReservation = () => {
        if (!getReservationDetails?.recurringId) return;
        navigate(
            `/reservations/ai-assistant/${getReservationDetails?.recurringId}`,
            {
                state: { name: 'viewAIReservation', type: 'reservations' },
            },
        );
    };

    const isAttending =
        ((getReservationDetails?.staffAttending?.find(
            (staff: any) => staff.id === loggedInUser?.sub,
        ) &&
            loggedInUser?.sub !== getReservationDetails?.booker?.id &&
            !loggedInUser?.role.includes('admin')) ||
            (getReservationDetails?.guest?.id !== loggedInUser?.company &&
                loggedInUser?.role !== 'admin')) &&
        !loggedInUser?.role.includes('owner');

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 1.25 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        {canShowCalendarTool ? (
                            <CustomTooltip title='Add to external calendar'>
                                <Box sx={iconButtonSx}>
                                    <CalendarMonthOutlinedIcon
                                        fontSize='small'
                                        style={{ pointerEvents: 'none' }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            opacity: 0,
                                        }}
                                    >
                                        <AddToCalendarButton
                                            name={titleName}
                                            label=''
                                            hideTextLabelButton
                                            hideCheckmark
                                            iCalFileName={icalName}
                                            description={calDescription}
                                            options={[
                                                'iCal|Download',
                                                'Google',
                                                'Outlook.com',
                                                'Microsoft365',
                                            ]}
                                            location={fullAddress}
                                            startDate={calendarStart}
                                            endDate={calendarEnd}
                                            startTime={
                                                getReservationDetails
                                                    ?.bookingInfo?.checkinTime
                                            }
                                            endTime={
                                                getReservationDetails
                                                    ?.bookingInfo?.checkoutTime
                                            }
                                            timeZone='Australia/Sydney'
                                            listStyle='modal'
                                            lightMode='bodyScheme'
                                            styleLight={calendarIconStyle}
                                            styleDark={calendarIconStyle}
                                        />
                                    </Box>
                                </Box>
                            </CustomTooltip>
                        ) : null}

                        {canShowDownloadTool ? (
                            <CustomTooltip title='Download reservation details'>
                                <Box sx={iconButtonSx}>
                                    <ShareReservation
                                        getReservationDetails={
                                            getReservationDetails
                                        }
                                        getExperienceDetails={
                                            getIndividualAsset
                                        }
                                    />
                                </Box>
                            </CustomTooltip>
                        ) : null}

                        <Typography
                            component='h2'
                            variant='body1'
                            sx={{
                                fontWeight: 700,
                                letterSpacing: 0.2,
                                fontSize: 15,
                                lineHeight: 1.15,
                                minWidth: 0,
                            }}
                        >
                            Reservation #{getReservationDetails?.id}
                        </Typography>
                    </Box>

                    <CustomChip
                        label={
                            <Typography
                                variant='body3'
                                sx={{ fontSize: 10.5, fontWeight: 900 }}
                            >
                                {formattedStatus}
                            </Typography>
                        }
                        variant='outlined'
                        sx={{
                            ...getStatusSx(status),
                            borderWidth: 1,
                            height: 18,
                            px: 0.6,
                            minWidth: 72,
                            borderRadius: 999,
                            flexShrink: 0,
                            '& .MuiChip-label': { px: 0.6, pb: 0.2 },
                        }}
                    />
                </Box>
            </Box>

            {getReservationDetails?.recurringId ? (
                <Box
                    role='button'
                    tabIndex={0}
                    onClick={openRecurringReservation}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                            openRecurringReservation();
                    }}
                    sx={{
                        mb: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        backgroundColor: theme.palette.neutral.main,
                        px: 1.5,
                        py: 1.1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            backgroundColor: theme.palette.primary.main,
                        },
                        '&:hover': {
                            backgroundColor:
                                theme.palette.custom.cardActionBackground,
                        },
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 900,
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: theme.palette.text.primary,
                            }}
                        >
                            Recurring reservation · #
                            {getReservationDetails?.recurringId}
                        </Typography>

                        <Typography
                            sx={{
                                mt: 0.2,
                                fontSize: 12,
                                fontWeight: 650,
                                color: theme.palette.text.secondary,
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            Created via 7DAY AI Assistant
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            flex: '0 0 auto',
                            borderRadius: 999,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            backgroundColor:
                                theme.palette.custom.cardActionBackground,
                            px: 1,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Typography sx={{ fontSize: 11, fontWeight: 900 }}>
                            View
                        </Typography>
                    </Box>
                </Box>
            ) : null}

            <Box sx={infoCardSx}>
                <Typography
                    variant='body1'
                    sx={{
                        fontWeight: 800,
                        color: theme.palette.text.primary,
                        borderBottom: `1px dashed ${theme.palette.info.main}`,
                        display: 'inline-block',
                    }}
                >
                    {dateRange}
                </Typography>

                <Typography
                    sx={{
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: 0.35,
                        color: theme.palette.text.secondary,
                        lineHeight: 1.2,
                        mt: 0.75,
                    }}
                >
                    {isCommercial ? 'Access window' : 'Check-in → Check-out'}
                </Typography>

                {timeRange !== '-' ? (
                    <Typography
                        sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: theme.palette.text.secondary,
                            mt: 0.4,
                            lineHeight: 1.2,
                        }}
                    >
                        {timeRange}
                    </Typography>
                ) : null}
            </Box>

            {canReview ||
            canBookAgain ||
            canReviewGuest ||
            existingGuestReview ||
            hasExistingReview ? (
                <Box sx={{ mt: 1.5, display: 'grid', gap: 1 }} key='actions'>
                    {!isOwner && (canReview || hasExistingReview) ? (
                        <Button
                            variant={
                                hasExistingReview ? 'outlined' : 'contained'
                            }
                            onClick={handleReview}
                            disabled={loggedInUser?.viewOnly}
                            sx={pillButtonSx}
                        >
                            {hasExistingReview
                                ? 'View review'
                                : 'Write a review'}
                        </Button>
                    ) : null}

                    {isOwner && hasExistingReview ? (
                        <Button
                            variant='outlined'
                            onClick={handleViewReviews}
                            disabled={loggedInUser?.viewOnly}
                            sx={pillButtonSx}
                        >
                            {`View review${allReviews?.length > 1 ? 's' : ''}`}
                        </Button>
                    ) : null}

                    {canReviewGuest || existingGuestReview ? (
                        <Button
                            variant={
                                existingGuestReview ? 'outlined' : 'contained'
                            }
                            onClick={handleOpenGuestReview}
                            disabled={loggedInUser?.viewOnly}
                            sx={pillButtonSx}
                        >
                            {isOwner
                                ? existingGuestReview
                                    ? existingGuestReview.published
                                        ? 'View guest review'
                                        : 'Edit guest review'
                                    : 'Review guest'
                                : 'View host review'}
                        </Button>
                    ) : null}

                    {canBookAgain ? (
                        <Button
                            color='primary'
                            variant='outlined'
                            sx={{
                                ...pillButtonSx,
                                height: 34,
                                fontWeight: 800,
                            }}
                            onClick={handleBookAgain}
                            disabled={isAttending}
                        >
                            Book again
                        </Button>
                    ) : null}
                </Box>
            ) : null}

            <ReviewModal
                open={openReviewModal}
                onClose={handleCloseReviewModal}
                bookingId={getReservationDetails?.id}
                experienceType={getIndividualAsset?.type}
                existingReview={existingReview}
            />

            <ReviewsModalForReservation
                open={openViewReviewsModal}
                onClose={handleCloseViewReviewsModal}
                bookingId={getReservationDetails?.id}
                reservationDetails={getReservationDetails}
                reviews={
                    isOwner
                        ? allReviews
                        : existingReview
                          ? [existingReview]
                          : isExistingReviewFetching
                            ? undefined
                            : []
                }
            />

            <GuestReviewModal
                open={openGuestReviewModal}
                onClose={handleCloseGuestReviewModal}
                bookingId={getReservationDetails?.id}
                guestName={getReservationDetails?.guest?.name}
                existingReview={existingGuestReview}
                readOnly={isGuest}
            />
        </Box>
    );
}
